/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import ContentLoader from 'react-content-loader';
import { formatRoute } from 'react-router-named-routes';
import { withTranslation } from 'react-i18next';
// material-ui icons
import Cancel from '@material-ui/icons/Cancel';
import Check from '@material-ui/icons/Check';
import Done from '@material-ui/icons/Done';
import Refresh from '@material-ui/icons/Refresh';
// material-ui/core components
import Checkbox from '@material-ui/core/Checkbox';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Tooltip, Zoom } from '@material-ui/core';
import extendedTablesStyle from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle';
import withStyles from '@material-ui/core/styles/withStyles';
// core components
import Badge from 'components/UI/Badge/Badge';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import GridItem from 'components/UI/Grid/GridItem';
import Snackbar from 'components/UI/Snackbar/Snackbar';
// global variables
import { reviewReportStatus } from 'config';
// our components
import Filter from 'components/ReviewReport/Filter';
import { LoadingAnimation } from 'components/common/Loading';
import Paginator from 'components/common/Paginator';
import RouterContext from 'helpers/routerContext';
import { TableLoader } from 'components/common/TableLoader';
import WithPermission from 'components/User/WithPermission';
import { REVIEW_REPORT, REVIEWS, USER } from 'routes/paths';
import dateFormat from 'helpers/dateHelper';

const resolvePermissions = [
  'reviews.resolve_reviewreport',
  'resolve_reviewreport',
];
const dismissPermissions = [
  'reviews.dismiss_reviewreport',
  'dismiss_reviewreport',
];
const viewReviewPermissions = ['reviews.view_review', 'view_review'];

const isOpenReport = report => report.status === reviewReportStatus.OPEN;

const truncate = (value, limit = 120) => {
  if (!value) {
    return '';
  }
  if (value.length <= limit) {
    return value;
  }
  return `${value.substring(0, limit)}...`;
};

class ReviewReportList extends React.Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    this.state = {
      selectedItems: [],
      showCollapseFilters: true,
    };
  }

  handleSelectAll = edges => {
    const { selectedItems } = this.state;
    const selectableItems = edges
      .filter(({ node }) => isOpenReport(node))
      .map(item => item.node.id);
    if (
      selectableItems.length > 0 &&
      selectedItems.length === selectableItems.length
    ) {
      this.setState({ selectedItems: [] });
    } else {
      this.setState({ selectedItems: selectableItems });
    }
  };

  handleSelectItem = id => {
    const { selectedItems } = this.state;
    if (selectedItems.indexOf(id) !== -1) {
      this.setState({
        selectedItems: selectedItems.filter(item => item !== id),
      });
    } else {
      this.setState({ selectedItems: [...selectedItems, id] });
    }
  };

  handleAction = (action, selectedItems) => {
    const { dismissReports, resolveReports } = this.props;
    if (action === 'resolve') {
      resolveReports(selectedItems);
    } else if (action === 'dismiss') {
      dismissReports(selectedItems);
    }
    this.setState({ selectedItems: [] });
  };

  handleReadUser = id => {
    const history = this.context;
    history.push(formatRoute(USER.DETAILS, { id }));
  };

  handleReadReport = id => {
    const history = this.context;
    history.push(formatRoute(REVIEW_REPORT.DETAILS, { id }));
  };

  handleReadReview = id => {
    const history = this.context;
    history.push(formatRoute(REVIEWS.DETAILS, { id }));
  };

  handleShowFilters = () => {
    const { showCollapseFilters } = this.state;
    this.setState({ showCollapseFilters: !showCollapseFilters });
  };

  renderUser = (user, idPrefix) => {
    const { classes, t } = this.props;
    if (!user) {
      return <span>{t('common.unknown')}</span>;
    }
    return (
      <WithPermission
        alt={<span>{user.fullname || user.id}</span>}
        allowPermissions={['view_user']}
      >
        <Button
          id={`${idPrefix}-${user.id}`}
          simple
          color="primary"
          style={{ padding: 0, margin: 0, textAlign: 'left' }}
          className={classes.adBtns}
          onClick={() => this.handleReadUser(user.id)}
        >
          {user.fullname || user.id}
        </Button>
      </WithPermission>
    );
  };

  renderStatus = status => {
    const { t } = this.props;
    if (!status) {
      return t('common.unknown');
    }
    return t(`review_report.status.${status}`);
  };

  renderReason = reason => {
    const { t } = this.props;
    if (!reason) {
      return t('common.unknown');
    }
    return t(`review_report.reasons.${reason}`);
  };

  renderColumns = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    const selectableItems = edges.filter(({ node }) => isOpenReport(node));
    return [
      {
        Header: (
          <Checkbox
            onClick={() => this.handleSelectAll(edges)}
            checked={
              selectableItems.length > 0 &&
              selectedItems.length === selectableItems.length
            }
            id="allCheckbox"
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        accessor: 'checkbox',
        sortable: false,
        filterable: false,
        maxWidth: 58,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('review_report.list.headers.report')}
          </span>
        ),
        accessor: 'report',
        sortable: false,
        filterable: false,
        minWidth: 260,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('review_report.list.headers.review')}
          </span>
        ),
        accessor: 'review',
        sortable: false,
        filterable: false,
        minWidth: 280,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('review_report.list.headers.users')}
          </span>
        ),
        accessor: 'users',
        sortable: false,
        filterable: false,
        minWidth: 240,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('review_report.list.headers.dates')}
          </span>
        ),
        accessor: 'dates',
        sortable: false,
        filterable: false,
        minWidth: 180,
      },
      {
        Header: (
          <span className={classes.headerField}>
            {t('review_report.list.headers.actions')}
          </span>
        ),
        accessor: 'actions',
        sortable: false,
        filterable: false,
        maxWidth: 120,
      },
      {
        expander: true,
        show: false,
      },
    ];
  };

  renderRows = edges => {
    const { selectedItems } = this.state;
    const { classes, t } = this.props;
    return edges.map(({ node }) => {
      const open = isOpenReport(node);
      return {
        id: node.id,
        checkbox: (
          <Checkbox
            onClick={() => this.handleSelectItem(node.id)}
            disabled={!open}
            checked={selectedItems.indexOf(node.id) !== -1}
            checkedIcon={<Check className={classes.checkedIcon} />}
            icon={<Check className={classes.uncheckedIcon} />}
            classes={{
              checked: classes.checked,
            }}
          />
        ),
        report: (
          <div>
            <Button
              id={`review-report-${node.id}`}
              simple
              color="primary"
              className={classes.adBtns}
              style={{ padding: 0, margin: 0, textAlign: 'left' }}
              onClick={() => this.handleReadReport(node.id)}
            >
              <b>{`${t('review_report.fields.id')}: ${node.id}`}</b>
            </Button>
            <p>
              <Badge color={open ? 'warning' : 'gray'}>
                {this.renderStatus(node.status)}
              </Badge>
            </p>
            <p>
              <b>{this.renderReason(node.reason)}</b>
            </p>
            <p>{truncate(node.comment) || t('common.unknown')}</p>
          </div>
        ),
        review: node.review ? (
          <div>
            <WithPermission
              alt={
                <p>
                  <b>
                    {`${t('review_report.fields.review_id')}: ${
                      node.review.id
                    }`}
                  </b>
                </p>
              }
              allowPermissions={viewReviewPermissions}
            >
              <Button
                id={`linked-review-${node.review.id}`}
                simple
                color="primary"
                className={classes.adBtns}
                style={{ padding: 0, margin: 0, textAlign: 'left' }}
                onClick={() => this.handleReadReview(node.review.id)}
              >
                <b>
                  {`${t('review_report.fields.review_id')}: ${node.review.id}`}
                </b>
              </Button>
            </WithPermission>
            <p>
              {`${t('review_report.fields.rate')}: ${node.review.rate ||
                t('common.unknown')}`}
            </p>
            <p>
              {`${t('review_report.fields.review_status')}: ${node.review
                .status || t('common.unknown')}`}
            </p>
            <p>{truncate(node.review.comment) || t('common.unknown')}</p>
          </div>
        ) : (
          <span>{t('common.unknown')}</span>
        ),
        users: (
          <div>
            <p>
              <b>{t('review_report.fields.reporter')}:</b>{' '}
              {this.renderUser(node.reporter, 'reporter')}
            </p>
            <p>
              <b>{t('review_report.fields.reviewer')}:</b>{' '}
              {this.renderUser(node.reviewer, 'reviewer')}
            </p>
            <p>
              <b>{t('review_report.fields.reviewee')}:</b>{' '}
              {this.renderUser(node.reviewee, 'reviewee')}
            </p>
            {node.reviewedBy && (
              <p>
                <b>{t('review_report.fields.reviewed_by')}:</b>{' '}
                {this.renderUser(node.reviewedBy, 'reviewed-by')}
              </p>
            )}
          </div>
        ),
        dates: (
          <div>
            <p>
              <b>{t('review_report.fields.created_on')}:</b>{' '}
              {node.createdOn ? dateFormat(node.createdOn, t) : '-'}
            </p>
            <p>
              <b>{t('review_report.fields.updated_on')}:</b>{' '}
              {node.updatedOn ? dateFormat(node.updatedOn, t) : '-'}
            </p>
            <p>
              <b>{t('review_report.fields.reviewed_on')}:</b>{' '}
              {node.reviewedOn ? dateFormat(node.reviewedOn, t) : '-'}
            </p>
          </div>
        ),
        actions: selectedItems.indexOf(node.id) === -1 && open && (
          <div className="actions-center">
            <WithPermission allowPermissions={resolvePermissions}>
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('review_report.actions.resolve')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="resolveReviewReport"
                  justIcon
                  color="success"
                  id={`resolve-review-report-${node.id}`}
                  onClick={() => this.handleAction('resolve', [node.id])}
                >
                  <Done />
                </Button>
              </Tooltip>
            </WithPermission>
            <WithPermission allowPermissions={dismissPermissions}>
              <Tooltip
                placement="top"
                TransitionComponent={Zoom}
                title={
                  <React.Fragment>
                    {t('review_report.actions.dismiss')}
                    <span className={classes.arrow} />
                  </React.Fragment>
                }
                classes={{
                  tooltip: classes.bootstrapTooltip,
                  tooltipPlacementTop: classes.bootstrapPlacementTop,
                }}
              >
                <Button
                  data-cy="dismissReviewReport"
                  justIcon
                  color="danger"
                  id={`dismiss-review-report-${node.id}`}
                  onClick={() => this.handleAction('dismiss', [node.id])}
                >
                  <Cancel />
                </Button>
              </Tooltip>
            </WithPermission>
          </div>
        ),
      };
    });
  };

  mapLoadingRows = (itemProcessing, edges) => {
    const loadingRows = {};
    edges.forEach((value, index) => {
      if (itemProcessing.includes(value.node.id)) {
        loadingRows[index] = true;
      }
    });
    return loadingRows;
  };

  renderNotificationMessage = () => {
    const { errors, failedIds, t } = this.props;
    if (errors.length > 0 && errors[0].messages.length > 0) {
      return errors[0].messages[0];
    }
    return t('common.failed_bulk_action', {
      count: failedIds.length,
    });
  };

  render() {
    const {
      data,
      loading,
      fullLoading,
      changeNumberOfRows,
      changePage,
      numberOfRowsSelected,
      paginationDirection,
      classes,
      filter,
      restartFilters,
      appliedFilter,
      itemProcessing,
      t,
      failedIds,
      showNotification,
      closeNotification,
      refresh,
    } = this.props;
    const { selectedItems, showCollapseFilters } = this.state;
    const edges = loading || data === undefined ? [] : data.reviewReports.edges;
    const rows = this.renderRows(edges);
    const cols = this.renderColumns(edges);
    const loadingRows = this.mapLoadingRows(itemProcessing, edges);
    const pageInfo =
      data && data.reviewReports ? data.reviewReports.pageInfo : {};
    const queryCount =
      data && data.reviewReports && data.reviewReports.meta
        ? data.reviewReports.meta.total
        : null;
    return (
      <React.Fragment>
        <Snackbar
          place="tc"
          color="danger"
          message={this.renderNotificationMessage()}
          open={showNotification}
          closeNotification={closeNotification}
          close
        />
        <div className={classes.pageContainer}>
          <GridContainer justify="space-between" alignItems="center">
            <GridItem>
              <h3 className={classes.title}>{t('review_report.list.title')}</h3>
            </GridItem>
            <GridItem>
              <Button
                color="primary"
                simple
                onClick={() => refresh()}
                id="refresh"
                key="refresh"
              >
                <Refresh />
                {t('ad.filters.update')}
              </Button>
            </GridItem>
          </GridContainer>

          <Filter
            filter={filter}
            appliedFilter={appliedFilter}
            restartFilters={restartFilters}
            showCollapseFilters={showCollapseFilters}
            showFilters={this.handleShowFilters}
          />

          <div className={classes.contentContainer}>
            <div
              className={[classes.loadingLineContainer, classes.topLine].join(
                ' ',
              )}
            >
              {' '}
              {fullLoading && (
                <LinearProgress
                  classes={{
                    barColorPrimary: classes.loadingLine,
                  }}
                />
              )}
            </div>
            <GridContainer>
              <GridItem
                className={[
                  classes.groupActions,
                  classes.loteActionsAnimation,
                  selectedItems.length > 0
                    ? classes.showLote
                    : classes.hideLote,
                ].join(' ')}
                xs={12}
              >
                {selectedItems.length > 0 && (
                  <React.Fragment>
                    <WithPermission allowPermissions={resolvePermissions}>
                      <Button
                        data-cy="resolveReviewReports"
                        color="success"
                        simple
                        onClick={() =>
                          this.handleAction('resolve', selectedItems)
                        }
                        id="resolveReviewReports"
                      >
                        <Done />
                        {t('review_report.actions.resolve')}
                      </Button>
                    </WithPermission>
                    <WithPermission allowPermissions={dismissPermissions}>
                      <Button
                        data-cy="dismissReviewReports"
                        color="danger"
                        simple
                        onClick={() =>
                          this.handleAction('dismiss', selectedItems)
                        }
                        id="dismissReviewReports"
                      >
                        <Cancel />
                        {t('review_report.actions.dismiss')}
                      </Button>
                    </WithPermission>
                  </React.Fragment>
                )}
              </GridItem>
              <GridItem
                className={[
                  classes.z2,
                  classes.loteActionsAnimation,
                  selectedItems.length > 0 ? classes.hideLote : '',
                ].join(' ')}
                xs={12}
              >
                <GridContainer justify="flex-end" alignItems="center">
                  <GridItem xs={12} sm={8} md={6} lg={4}>
                    {!fullLoading ? (
                      <Paginator
                        pageInfo={pageInfo}
                        queryCount={queryCount}
                        changePage={changePage}
                        changeNumberOfRows={changeNumberOfRows}
                        paginationDirection={paginationDirection}
                        numberOfRowsSelected={numberOfRowsSelected}
                      />
                    ) : (
                      <ContentLoader height={67} width={327} speed={2}>
                        <path
                          // eslint-disable-next-line max-len
                          d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                          fill="#D8D8D8"
                        />
                      </ContentLoader>
                    )}
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem xs={12} className={classes.z3}>
                <GridContainer>
                  <GridItem xs={12}>
                    {loading ? (
                      <TableLoader />
                    ) : (
                      <ReactTable
                        data={rows}
                        filterable
                        columns={cols}
                        loading={loading}
                        pageSize={
                          edges.length < numberOfRowsSelected
                            ? edges.length
                            : numberOfRowsSelected
                        }
                        showPagination={false}
                        className={['-striped -highlight', classes.tbl].join(
                          ' ',
                        )}
                        expanded={loadingRows}
                        SubComponent={() => (
                          <div className={classes.loading}>
                            <LoadingAnimation />
                          </div>
                        )}
                        getTrProps={(state, rowInfo) => ({
                          style: {
                            backgroundColor:
                              rowInfo &&
                              selectedItems.indexOf(rowInfo.original.id) !== -1
                                ? 'rgba(171, 221, 250, .8)'
                                : '',
                            boxShadow:
                              rowInfo &&
                              failedIds.indexOf(rowInfo.original.id) !== -1
                                ? 'inset 4px 0 0 red'
                                : '',
                          },
                        })}
                        noDataText={t('common.empty_list')}
                        minRows={8}
                      />
                    )}
                  </GridItem>
                </GridContainer>
                <GridContainer justify="flex-end" alignItems="center">
                  <GridItem xs={12} sm={8} md={6} lg={4}>
                    {!fullLoading ? (
                      <Paginator
                        pageInfo={pageInfo}
                        queryCount={queryCount}
                        changePage={changePage}
                        changeNumberOfRows={changeNumberOfRows}
                        paginationDirection={paginationDirection}
                        numberOfRowsSelected={numberOfRowsSelected}
                      />
                    ) : (
                      <ContentLoader height={67} width={327} speed={2}>
                        <path
                          // eslint-disable-next-line max-len
                          d="M199,16 L304,16 C307.313708,16 310,18.6862915 310,22 L310,46 C310,49.3137085 307.313,52 304,52 L199,52 C195.686292,52 193,49.3137085 193,46 L193,22 C193,18.6862915 195.686292,16 199,16 Z M25.9861352,27.0121317 L70.0138648,27.0121317 C74.4244855,27.0121317 78,30.5876462 78,34.9982669 C78,39.4088876 74.4244855,42.9844021 70.0138648,42.9844021 L25.9861352,42.9844021 C21.5755145,42.9844021 18,39.4088876 18,34.9982669 C18,30.5876462 21.5755145,27.0121317 25.9861352,27.0121317 Z M103.986135,27.0121317 L148.013865,27.0121317 C152.424485,27.0121317 156,30.5876462 156,34.9982669 C156,39.4088876 152.424485,42.9844021 148.013865,42.9844021 L103.986135,42.9844021 C99.5755145,42.9844021 96,39.4088876 96,34.9982669 C96,30.5876462 99.5755145,27.0121317 103.986135,27.0121317 Z"
                          id="Combined-Shape"
                          fill="#D8D8D8"
                        />
                      </ContentLoader>
                    )}
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

ReviewReportList.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  itemProcessing: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.shape({}).isRequired,
  numberOfRowsSelected: PropTypes.number.isRequired,
  paginationDirection: PropTypes.string,
  resolveReports: PropTypes.func.isRequired,
  dismissReports: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  changeNumberOfRows: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  fullLoading: PropTypes.bool.isRequired,
  appliedFilter: PropTypes.shape({}).isRequired,
  filter: PropTypes.func.isRequired,
  refresh: PropTypes.func.isRequired,
  restartFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  failedIds: PropTypes.instanceOf(Array).isRequired,
  errors: PropTypes.instanceOf(Array).isRequired,
  showNotification: PropTypes.bool.isRequired,
  closeNotification: PropTypes.func.isRequired,
};

ReviewReportList.defaultProps = {
  paginationDirection: null,
};

const ReviewReportWrapper = props => <ReviewReportList {...props} />;

export default withTranslation()(
  withStyles(extendedTablesStyle)(ReviewReportWrapper),
);
